import { useCallback, useEffect, useRef, useState } from "react";
import { isAbortError } from "@fortex/api";

export type LoadState<T> = {
  data: T | null;
  error: unknown | null;
  loading: boolean;
  reload: () => void;
  setData: (updater: (previous: T | null) => T | null) => void;
};

type Settled<T> = { key: string; data: T | null; error: unknown | null };

export function useLoad<T>(loader: (signal: AbortSignal) => Promise<T>, deps: (string | number | boolean | null)[]): LoadState<T> {
  const [version, setVersion] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const loaderRef = useRef(loader);
  const key = `${version}|${deps.map(String).join("\u0000")}`;

  useEffect(() => {
    loaderRef.current = loader;
  });

  useEffect(() => {
    const controller = new AbortController();

    loaderRef
      .current(controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setSettled({ key, data: result, error: null });
      })
      .catch(reason => {
        if (controller.signal.aborted || isAbortError(reason)) return;

        setSettled(previous => ({ key, data: previous?.data ?? null, error: reason }));
      });

    return () => controller.abort();
  }, [key]);

  const reload = useCallback(() => setVersion(a => a + 1), []);
  const setData = useCallback((updater: (previous: T | null) => T | null) => setSettled(previous => (previous === null ? previous : { ...previous, data: updater(previous.data) })), []);
  const current = settled !== null && settled.key === key ? settled : null;

  return { data: current?.data ?? settled?.data ?? null, error: current?.error ?? null, loading: current === null, reload, setData };
}
